import React, { useEffect } from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, CardMedia, TextField, Button } from '@mui/material';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import BuildIcon from '@mui/icons-material/Build';
import EngineeringIcon from '@mui/icons-material/Engineering';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ConstructionIcon from '@mui/icons-material/Construction';
import ShieldIcon from '@mui/icons-material/Shield';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';

// Fix for default marker icons in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// ===== HERO SECTION =====
const HeroSection = () => {
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: '#f5f5f5',
        minHeight: { xs: '80vh', md: '90vh' },
        display: 'flex',
        alignItems: 'center',
        py: { xs: 4, md: 8 },
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: { xs: 'block', md: 'flex' },
            alignItems: 'center',
            minHeight: { xs: 'auto', md: '500px' },
            gap: { md: 6 },
          }}
        >
          <Box
            sx={{
              flex: { xs: 'none', md: 1 },
              textAlign: { xs: 'center', sm: 'center', md: 'left' },
              maxWidth: { xs: '100%', sm: '100%', md: '500px', lg: '600px' },
              mx: { xs: 'auto', md: 0 },
              mb: { xs: 4, md: 0 },
            }}
          >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: 'primary.main',
                  color: 'white',
                  borderRadius: '20px',
                  px: 3,
                  py: 1,
                  mb: 3,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                Elektromeisterbetrieb
              </Box>

              <Typography
                variant="h1"
                sx={{
                  color: 'text.primary',
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                }}
              >
                DAD-Service
              </Typography>

              <Typography
                variant="h3"
                sx={{
                  color: 'text.secondary',
                  mb: 3,
                  fontSize: { xs: '1.25rem', md: '1.75rem' },
                  fontWeight: 400,
                }}
              >
                Elektro- und Gebäudetechnik im Rhein-Main-Gebiet
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  mb: 4,
                  fontSize: '1.1rem',
                  lineHeight: 1.6,
                  maxWidth: { xs: '100%', md: '500px' },
                }}
              >
                Unser Team garantiert erstklassigen Service, Qualität und
                Kundenzufriedenheit. Wir sind Ihr zuverlässiger Partner für alle
                elektrotechnischen Arbeiten.
              </Typography>

              <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 } }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: { xs: 'none', sm: 1 },
                  justifyContent: { xs: 'center', sm: 'flex-start' },
                  mb: { xs: 0, sm: 2 }
                }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '8px',
                      backgroundColor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      flexShrink: 0,
                    }}
                  >
                    <EmailIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      E-mail
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500 }}>
                      info@dad-service.de
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: { xs: 'none', sm: 1 },
                  justifyContent: { xs: 'center', sm: 'flex-start' }
                }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '8px',
                      backgroundColor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      flexShrink: 0,
                    }}
                  >
                    <PhoneIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      Telefon
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500 }}>
                      0159 067 949 85
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: { xs: 'center', sm: 'flex-start' }, alignItems: { xs: 'center', sm: 'center' } }}>
                <Box
                  onClick={() => scrollToSection('contact-form')}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    backgroundColor: 'primary.main',
                    color: 'white',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    border: 'none',
                    minWidth: { xs: '200px', sm: 'auto' },
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  }}
                >
                  Kostenloses Angebot
                </Box>

                <Box
                  onClick={() => scrollToSection('services')}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    border: '1px solid',
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    minWidth: { xs: '200px', sm: 'auto' },
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                    },
                  }}
                >
                  Unsere Leistungen
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
              flex: { xs: 'none', md: 1 },
              display: { xs: 'none', sm: 'none', md: 'flex' },
                justifyContent: 'center',
                alignItems: 'center',
              minHeight: { md: '400px' },
              }}
            >
              <Box
                component="img"
                src="/images/pexels-ksenia-chernaya-5691590.jpg"
                alt="Elektrotechnik und Installation"
                sx={{
                  width: '100%',
                height: '600px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                }}
              />
            </Box>
        </Box>
      </Container>
    </Box>
  );
};

// ===== SERVICES SECTION =====
const services = [
  {
    title: 'Beleuchtung',
    subtitle: 'Professionelle Lichtinstallationen',
    description: 'Moderne LED-Technik und energieeffiziente Beleuchtungslösungen für Ihr Zuhause und Gewerbe.',
    icon: LightbulbIcon,
  },
  {
    title: 'Wartung',
    subtitle: 'Regelmäßige Systempflege',
    description: 'Umfassende Wartung und Instandhaltung Ihrer elektrischen Anlagen für maximale Sicherheit.',
    icon: SettingsIcon,
  },
  {
    title: 'Service',
    subtitle: 'Schnelle Problemlösungen',
    description: '24/7 Notdienst und schnelle Reparaturen für alle elektrischen Störungen und Ausfälle.',
    icon: ConstructionIcon,
  },
  {
    title: 'Installation',
    subtitle: 'Neuinstallationen',
    description: 'Professionelle Installation neuer elektrischer Systeme nach aktuellen Sicherheitsstandards.',
    icon: EngineeringIcon,
  },
  {
    title: 'Haustechnik',
    subtitle: 'Intelligente Gebäudesysteme',
    description: 'Moderne Haustechnik für Smart Home, Alarmanlagen und vernetzte Gebäudetechnik.',
    icon: ElectricBoltIcon,
  },
  {
    title: 'Sicherheit',
    subtitle: 'Brandschutz & Sicherheit',
    description: 'Brandschutzsysteme, Notbeleuchtung und Sicherheitsinstallationen für höchste Sicherheit.',
    icon: ShieldIcon,
  },
];

const ServicesSection = () => {
  return (
    <Box
      id="services"
      sx={{
        py: { xs: 6, md: 10 },
        backgroundColor: 'white',
        scrollMarginTop: '80px',
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, md: 4 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              color: 'primary.main',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Unsere Expertise
          </Typography>

          <Typography
            variant="h2"
            sx={{
              mb: 3,
              color: 'text.primary',
              fontSize: { xs: '2rem', md: '2.5rem' },
            }}
          >
            Unsere Dienstleistungen
          </Typography>

          <Typography
            variant="body1"
            sx={{
              maxWidth: '600px',
              mx: 'auto',
              color: 'text.secondary',
              fontSize: '1.1rem',
              lineHeight: 1.6,
            }}
          >
            Mit jahrelanger Erfahrung und modernster Technik bieten wir Ihnen
            umfassende Lösungen für alle Ihre elektrotechnischen Bedürfnisse.
          </Typography>
        </Box>

        <Box sx={{ maxWidth: '1400px', mx: 'auto', px: { xs: 2, md: 4 } }}>
          <Grid container spacing={4} justifyContent="center" sx={{ alignItems: 'stretch' }}>
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Grid item xs={12} sm={6} md={4} key={service.title} sx={{ display: 'flex', alignItems: 'stretch' }}>
                  <Card
                    sx={{
                      width: '500px',
                      height: '340px',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      border: '1px solid #e0e0e0',
                      borderRadius: '12px',
                      '&:hover': {
                        boxShadow: '0 12px 30px rgba(0, 0, 0, 0.15)',
                        transform: 'translateY(-4px)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 80,
                        height: 80,
                        borderRadius: '16px',
                        backgroundColor: 'primary.main',
                        mx: 'auto',
                        mt: 3,
                        mb: 2,
                        boxShadow: '0 4px 12px rgba(26, 54, 93, 0.3)',
                      }}
                    >
                      <IconComponent sx={{ color: 'white', fontSize: '2.5rem' }} />
                    </Box>

                    <CardContent
                      sx={{
                        flexGrow: 1,
                        p: 3,
                        pt: 0,
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        height: 240,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: 'text.primary',
                            mb: 1,
                            fontSize: '1.25rem',
                            minHeight: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {service.title}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: 'primary.main',
                            fontWeight: 600,
                            mb: 2,
                            fontSize: '0.9rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            minHeight: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {service.subtitle}
                        </Typography>
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.6,
                          fontSize: '0.95rem',
                          flexGrow: 1,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {service.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

// ===== GALLERY SECTION =====
const galleryImages = [
  {
    id: 1,
    src: '/images/karolina-grabowska.jpg',
    alt: 'Professionelle Elektroinstallation',
    title: 'Moderne Schaltanlagen',
  },
  {
    id: 2,
    src: '/images/pexels-annas-zakaria-10871737.jpg',
    alt: 'Elektrotechnische Arbeiten',
    title: 'Gebäudetechnik',
  },
  {
    id: 3,
    src: '/images/pexels-fatih-yurtman-17842832.jpg',
    alt: 'LED-Beleuchtung Installation',
    title: 'Energieeffiziente Beleuchtung',
  },
  {
    id: 4,
    src: '/images/markus-spiske.jpg',
    alt: 'Sicherheitsinstallationen',
    title: 'Brandschutzsysteme',
  },
];

const GallerySection = () => {
  return (
    <Box
      id="gallery"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: '#f5f5f5',
        px: { xs: 2, md: 0 },
        scrollMarginTop: '80px',
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, md: 4 } }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              color: 'primary.main',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Unsere Projekte
          </Typography>

          <Typography
            variant="h2"
            sx={{
              mb: 3,
              color: 'text.primary',
              fontSize: { xs: '1.8rem', md: '2.5rem' },
              px: { xs: 2, md: 0 },
            }}
          >
            Professionelle Elektroarbeiten
          </Typography>

          <Typography
            variant="body1"
            sx={{
              maxWidth: '600px',
              mx: 'auto',
              color: 'text.secondary',
              fontSize: { xs: '1rem', md: '1.1rem' },
              lineHeight: 1.6,
              px: { xs: 2, md: 0 },
            }}
          >
            Entdecken Sie unsere professionellen Elektroarbeiten und sehen Sie,
            wie wir höchste Qualitätsstandards in jedem Projekt umsetzen.
          </Typography>
        </Box>

        <Box sx={{ maxWidth: '1400px', mx: 'auto', px: { xs: 1, md: 4 } }}>
          <Grid container spacing={3} justifyContent="center">
            {galleryImages.map((image) => (
              <Grid item xs={12} sm={6} md={6} lg={6} key={image.id} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Card
                  sx={{
                    width: { xs: '280px', sm: '300px', md: '320px', lg: '340px' },
                    maxWidth: '100%',
                    height: { xs: '280px', sm: '300px' },
                    borderRadius: '12px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #e0e0e0',
                    display: 'flex',
                    flexDirection: 'column',
                    mx: 'auto',
                    '&:hover': {
                      boxShadow: '0 12px 30px rgba(0, 0, 0, 0.15)',
                      transform: 'translateY(-4px)',
                      '& .gallery-image': {
                        transform: 'scale(1.05)',
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: { xs: 200, sm: 240 }, // Responsive height for consistent image area
                      position: 'relative',
                      overflow: 'hidden',
                      backgroundColor: '#f0f0f0',
                    }}
                  >
                    <Box
                      component="img"
                      src={image.src}
                      alt={image.alt}
                      className="gallery-image"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center',
                        display: 'block',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                  <CardContent
                    sx={{
                      height: 100, // Fixed height for content area (340 - 240 = 100)
                      p: 3,
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: 'text.primary',
                        textAlign: 'center',
                        lineHeight: 1.4,
                      }}
                    >
                      {image.title}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box sx={{ textAlign: 'center', mt: 10, px: { xs: 2, md: 3 } }}>
          <Typography
            variant="h3"
            sx={{
              mb: 6,
              color: 'text.primary',
              fontSize: { xs: '1.8rem', md: '2.2rem' },
              fontWeight: 600,
            }}
          >
            Warum DAD-Service?
          </Typography>

          <Box sx={{ maxWidth: { xs: '400px', sm: '600px', md: '900px' }, mx: 'auto' }}>
            <Grid container spacing={3} justifyContent="center" sx={{ alignItems: 'center' }}>
              <Grid item xs={6} sm={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box
                  sx={{
                    width: { xs: '120px', sm: '140px', md: '150px' },
                    height: { xs: '120px', sm: '140px', md: '150px' },
                    p: { xs: 2, md: 3 },
                    textAlign: 'center',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      color: 'primary.main',
                      mb: 1,
                      fontSize: { xs: '1.8rem', md: '2.2rem' }
                    }}
                  >
                    500+
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: { xs: '0.8rem', md: '0.9rem' },
                      lineHeight: 1.4,
                    }}
                  >
                    Projekte
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6} sm={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box
                  sx={{
                    width: { xs: '120px', sm: '140px', md: '150px' },
                    height: { xs: '120px', sm: '140px', md: '150px' },
                    p: { xs: 2, md: 3 },
                    textAlign: 'center',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      color: 'primary.main',
                      mb: 1,
                      fontSize: { xs: '1.8rem', md: '2.2rem' }
                    }}
                  >
                    15+
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: { xs: '0.8rem', md: '0.9rem' },
                      lineHeight: 1.4,
                    }}
                  >
                    Jahre<br />Erfahrung
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6} sm={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box
                  sx={{
                    width: { xs: '120px', sm: '140px', md: '150px' },
                    height: { xs: '120px', sm: '140px', md: '150px' },
                    p: { xs: 2, md: 3 },
                    textAlign: 'center',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      color: 'primary.main',
                      mb: 1,
                      fontSize: { xs: '1.8rem', md: '2.2rem' }
                    }}
                  >
                    24/7
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: { xs: '0.8rem', md: '0.9rem' },
                      lineHeight: 1.4,
                    }}
                  >
                    Notdienst
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6} sm={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box
                  sx={{
                    width: { xs: '120px', sm: '140px', md: '150px' },
                    height: { xs: '120px', sm: '140px', md: '150px' },
                    p: { xs: 2, md: 3 },
                    textAlign: 'center',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      color: 'primary.main',
                      mb: 1,
                      fontSize: { xs: '1.8rem', md: '2.2rem' }
                    }}
                  >
                    100%
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: { xs: '0.8rem', md: '0.9rem' },
                      lineHeight: 1.4,
                    }}
                  >
                    Zufriedenheit
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

// ===== CONTACT SECTION =====
const ContactSection = () => {
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    message: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      alert('Bitte füllen Sie alle Felder aus.');
      return;
    }

    // Create mailto link with form data
    const subject = encodeURIComponent('Kontaktanfrage von Website');
    const body = encodeURIComponent(
      `Name: ${formData.name}\n` +
      `Email: ${formData.email}\n\n` +
      `Nachricht:\n${formData.message}`
    );

    const mailtoUrl = `mailto:info@dad-service.de?subject=${subject}&body=${body}`;

    // Open email client
    window.location.href = mailtoUrl;

    // Clear form after a short delay to give user feedback
    setTimeout(() => {
      setFormData({ name: '', email: '', message: '' });
    }, 1000);
  };

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        backgroundColor: '#f9f9f9',
        scrollMarginTop: '80px',
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            backgroundColor: 'primary.main',
            borderRadius: '12px',
            p: { xs: 4, md: 6 },
            textAlign: 'center',
            mb: 6,
            color: 'white',
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '1.8rem', md: '2.2rem' },
            }}
          >
            Schneller und sicherer Service
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 500,
              mb: 3,
              fontSize: { xs: '1.2rem', md: '1.5rem' },
            }}
          >
            Rufen Sie uns jederzeit an:
          </Typography>
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: '8px',
              px: 4,
              py: 2,
              display: 'inline-flex',
              alignItems: 'center',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            }}
          >
            <PhoneIcon sx={{ mr: 2, color: 'primary.main', fontSize: '1.5rem' }} />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: 'primary.main',
              }}
            >
              <a
                href="tel:015906794985"
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                0159 067 949 85
              </a>
            </Typography>
          </Box>
        </Box>

          {/* Unified Locations Section */}
          <Box sx={{ textAlign: 'center', mb: 4, mt: 6 }}>
            <Typography
              variant="h3"
              sx={{
                mb: 2,
                fontWeight: 700,
                color: 'text.primary',
                fontSize: { xs: '2rem', md: '2.5rem' },
              }}
            >
              Unsere Standorte
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                fontSize: '1.1rem',
                lineHeight: 1.6,
                maxWidth: '600px',
                mx: 'auto',
              }}
            >
              Finden Sie uns im Rhein-Main-Gebiet mit unseren beiden Niederlassungen
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 6 }}>
            <Box sx={{ flex: 1 }}>
              <Card
                sx={{
                  height: '500px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                }}
              >
                <MapContainer
                  center={[50.1109, 8.6821]}
                  zoom={8}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  <Marker position={[50.1109, 8.6821]}>
                    <Popup>
                      <strong>🏢 Hauptniederlassung</strong><br />
                      <strong>DAD-Service GbR</strong><br />
                      Breslauerstraße 44<br />
                      60598 Frankfurt<br />
                      <em>Elektro- und Gebäudetechnik</em>
                    </Popup>
                  </Marker>
                  <Marker position={[49.9807, 9.1350]}>
                    <Popup>
                      <strong>🏢 Zweigniederlassung</strong><br />
                      <strong>DAD-Service GbR</strong><br />
                      Tauberstraße 54<br />
                      63741 Aschaffenburg<br />
                      <em>Elektro- und Gebäudetechnik</em>
                    </Popup>
                  </Marker>
                </MapContainer>
              </Card>
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <Card
                sx={{
                  height: '500px',
                  borderRadius: '12px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  p: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: 4,
                  alignItems: 'center',
                }}
              >
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                    Hauptniederlassung
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    DAD-Service GbR
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '1.1rem', lineHeight: 1.6 }}>
                    Breslauerstraße 44
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '1.1rem', lineHeight: 1.6 }}>
                    60598 Frankfurt
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                    Zweigniederlassung
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    DAD-Service GbR
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '1.1rem', lineHeight: 1.6 }}>
                    Tauberstraße 54
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '1.1rem', lineHeight: 1.6 }}>
                    63741 Aschaffenburg
                  </Typography>
                </Box>
              </Card>
            </Box>
          </Box>

        {/* Contact Form - Full Width */}
        <Box id="contact-form" sx={{ mb: 6 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h3"
              sx={{
                mb: 2,
                fontWeight: 700,
                color: 'text.primary',
                fontSize: { xs: '2rem', md: '2.5rem' },
              }}
            >
              Kontaktieren Sie uns
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                fontSize: '1.1rem',
                lineHeight: 1.6,
                maxWidth: '600px',
                mx: 'auto',
              }}
            >
              Haben Sie Fragen oder benötigen Sie ein Angebot? Schreiben Sie uns gerne eine Nachricht.
            </Typography>
          </Box>

          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
          <Card
            sx={{
              p: { xs: 3, md: 4 },
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              borderRadius: '12px',
                backgroundColor: 'white',
            }}
          >
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                }}
              >
                <TextField
                  fullWidth
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                  label="Ihr Name"
                  variant="outlined"
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                    }}
                  />

                <TextField
                  fullWidth
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                  label="Ihre E-Mail Adresse"
                  variant="outlined"
                    required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                    }}
                  />

                <TextField
                  fullWidth
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                  label="Ihre Nachricht"
                  variant="outlined"
                  multiline
                    rows={4}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                    }}
                  />

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    sx={{
                      px: 6,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      borderRadius: '8px',
                      textTransform: 'none',
                      backgroundColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    }}
                  >
                    Nachricht senden
                  </Button>
                </Box>
            </Box>
          </Card>
          </Box>

        </Box>
      </Container>
    </Box>
  );
};

// ===== MAIN LANDING PAGE =====
const LandingPage = () => {
  useEffect(() => {
    // Handle hash-based scrolling for anchor links
    const hash = window.location.hash;
    if (hash) {
      const element = document.getElementById(hash.substring(1));
      if (element) {
        // Small delay to ensure the page is fully loaded
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, []);

  return (
    <Box>
      <HeroSection />
      <ServicesSection />
      <GallerySection />
      <ContactSection />
    </Box>
  );
};

export default LandingPage;
